import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { toast } from 'sonner';
import BadgeAvatar from '../components/BadgeAvatar';
import ForumLoginModal from '../components/ForumLoginModal';

const MockExam = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [mockExam, setMockExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isForumLoginOpen, setIsForumLoginOpen] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    fetchMockExam();
  }, [user]);

  useEffect(() => {
    if (mockExam && !submitted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [mockExam, submitted, timeLeft]);

  const fetchMockExam = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mock-exams/featured');
      setMockExam(response.data);
      setTimeLeft(response.data.time_limit || 8100);
      setAnswers(new Array(response.data.questions.length).fill(-1));
      setStartTime(Date.now());
    } catch (error) {
      console.error('Error fetching mock exam:', error);
      toast.error('Failed to load mock exam');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please log in to submit your exam');
      return;
    }

    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      const response = await api.post('/mock-exams/submit', {
        mock_exam_id: mockExam.id,
        answers: answers,
        time_taken: timeTaken
      });
      
      setResults(response.data);
      setSubmitted(true);
      toast.success(`Exam submitted! Score: ${response.data.score}%`);
    } catch (error) {
      console.error('Error submitting mock exam:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (showLoginModal && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
          <div className='w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6'>
            <svg className='w-10 h-10 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sign Up for Mock Exams
          </h2>
          <p className="text-gray-700 mb-6 text-lg text-center">
            Create a free forum account to access our comprehensive 90-question Case Study Mock Exam!
          </p>
          <div className='space-y-4 mb-6'>
            <div className='flex items-start space-x-3'>
              <svg className='w-6 h-6 text-purple-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <p className='text-gray-700'>Take comprehensive mock exams (90 questions)</p>
            </div>
            <div className='flex items-start space-x-3'>
              <svg className='w-6 h-6 text-purple-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <p className='text-gray-700'>Study 15 diverse case scenarios</p>
            </div>
            <div className='flex items-start space-x-3'>
              <svg className='w-6 h-6 text-purple-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <p className='text-gray-700'>Get detailed explanations for every answer</p>
            </div>
            <div className='flex items-start space-x-3'>
              <svg className='w-6 h-6 text-purple-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <p className='text-gray-700'>Track your progress and scores</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsForumLoginOpen(true)}
              className='bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-lg px-8 py-6 rounded-full shadow-lg'
            >
              Create Forum Account
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className='rounded-full px-8 py-6'
            >
              Back to Dashboard
            </Button>
          </div>
          <p className='text-sm text-gray-600 mt-4 text-center'>
            Already have an account? <button onClick={() => setIsForumLoginOpen(true)} className='text-purple-600 hover:text-purple-700 font-semibold'>Click here to log in</button>
          </p>
          
          {/* ForumLoginModal for actual authentication */}
          <ForumLoginModal 
            isOpen={isForumLoginOpen}
            onClose={() => {
              setIsForumLoginOpen(false);
              navigate('/dashboard');
            }}
            onSuccess={(userData) => {
              setUser(userData);
              setShowLoginModal(false);
              setIsForumLoginOpen(false);
            }}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mock exam...</p>
        </div>
      </div>
    );
  }

  if (!mockExam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No mock exam available</p>
          <Link to="/dashboard">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const totalQuestions = mockExam.questions.length;
  const answeredCount = answers.filter(a => a !== -1).length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Case Study Mock Exam</span>
            </div>
            <div className="flex items-center space-x-4">
              {!submitted && (
                <div className={`text-lg font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatTime(timeLeft)}
                </div>
              )}
              {user && <BadgeAvatar user={user} size="md" />}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {!submitted ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-700">
                  Question {currentQuestion + 1} of {totalQuestions}
                </span>
                <span className="text-sm text-gray-600">
                  Answered: {answeredCount}/{totalQuestions}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
                />
              </div>
            </div>

            {/* Show Case Study at the start of each set of 6 questions */}
            {currentQuestion % 6 === 0 && (
              <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Case Study {Math.floor(currentQuestion / 6) + 1} of 15
                    </h3>
                    <p className="text-sm text-gray-600">Read carefully before answering the questions</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                    {mockExam.case_scenarios[Math.floor(currentQuestion / 6)]}
                  </p>
                </div>
                <div className="mt-4 text-sm text-indigo-600 font-medium">
                  📝 You will answer 6 questions about this case study
                </div>
              </div>
            )}

            {/* Question */}
            <div className="mb-8">
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
                    {mockExam.questions[currentQuestion].section.replace('_', ' ')}
                  </span>
                  <span className="text-xs font-semibold text-gray-500">
                    Question {(currentQuestion % 6) + 1} of 6 (Case Study {Math.floor(currentQuestion / 6) + 1})
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mt-2">
                  {mockExam.questions[currentQuestion].question}
                </h2>
              </div>

              {/* Options */}
              <div className="space-y-3">
                {mockExam.questions[currentQuestion].options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      answers[currentQuestion] === idx
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <span className="font-bold text-gray-700">{String.fromCharCode(65 + idx)}.</span>
                      <span className="text-gray-900">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
              >
                ← Previous
              </Button>
              
              {currentQuestion < totalQuestions - 1 ? (
                <Button
                  onClick={() => setCurrentQuestion(Math.min(totalQuestions - 1, currentQuestion + 1))}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  Next →
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  Submit Exam
                </Button>
              )}
            </div>

            {/* Question Grid */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Quick Navigation:</h3>
              <div className="grid grid-cols-10 gap-2">
                {answers.map((answer, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestion(idx)}
                    className={`aspect-square rounded-lg text-sm font-semibold transition-all ${
                      idx === currentQuestion
                        ? 'bg-purple-600 text-white'
                        : answer !== -1
                        ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
                  results.score >= 70 ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <span className={`text-4xl font-bold ${
                    results.score >= 70 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {results.score}%
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {results.score >= 70 ? 'Great Job!' : 'Keep Practicing!'}
                </h2>
                <p className="text-gray-600">
                  You got {results.correct_count} out of {results.total_questions} questions correct
                </p>
              </div>

              <Link to="/dashboard">
                <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700">
                  Back to Dashboard
                </Button>
              </Link>
            </div>

            {/* Detailed Results */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Review Your Answers</h3>
              
              {results.results.map((result, idx) => {
                const caseStudyIndex = Math.floor(idx / 6);
                const isFirstQuestionOfCase = idx % 6 === 0;
                
                return (
                  <div key={idx}>
                    {/* Show case study at the start of each set of 6 questions */}
                    {isFirstQuestionOfCase && (
                      <div className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-900">
                              Case Study {caseStudyIndex + 1} of 15
                            </h4>
                            <p className="text-sm text-gray-600">Questions {idx + 1}-{idx + 6}</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                            {mockExam.case_scenarios[caseStudyIndex]}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Question result */}
                    <div className={`mb-6 pb-6 border-b border-gray-200 last:border-b-0 ${
                      result.is_correct ? 'bg-green-50' : 'bg-red-50'
                    } rounded-lg p-4`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Question {result.question_number} - {result.section.replace('_', ' ')} (Case {caseStudyIndex + 1}, Q{(idx % 6) + 1})
                          </span>
                          <h4 className="text-lg font-bold text-gray-900 mt-1">
                            {result.question}
                          </h4>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          result.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          {result.is_correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      </div>

                      {/* Options */}
                      <div className="space-y-2 mb-4">
                    {result.options.map((option, optIdx) => (
                      <div
                        key={optIdx}
                        className={`p-3 rounded-lg ${
                          optIdx === result.correct_answer
                            ? 'bg-green-200 border-2 border-green-500'
                            : optIdx === result.user_answer && !result.is_correct
                            ? 'bg-red-200 border-2 border-red-500'
                            : 'bg-white border border-gray-200'
                        }`}
                      >
                        <div className="flex items-start space-x-2">
                          <span className="font-bold text-gray-700">{String.fromCharCode(65 + optIdx)}.</span>
                          <span className="text-gray-900">{option}</span>
                          {optIdx === result.correct_answer && (
                            <span className="ml-auto text-green-600 font-semibold">✓ Correct Answer</span>
                          )}
                          {optIdx === result.user_answer && !result.is_correct && (
                            <span className="ml-auto text-red-600 font-semibold">Your Answer</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Explanations */}
                  <div className="space-y-3">
                    <div className="bg-green-100 border-l-4 border-green-500 p-3 rounded">
                      <p className="text-sm font-semibold text-green-900 mb-1">Why the correct answer is correct:</p>
                      <p className="text-sm text-green-800">{result.explanation_correct || 'No explanation provided.'}</p>
                    </div>
                    
                    {!result.is_correct && result.explanation_incorrect && result.explanation_incorrect.length > 0 && (
                      <div className="bg-red-100 border-l-4 border-red-500 p-3 rounded">
                        <p className="text-sm font-semibold text-red-900 mb-1">Why your answer is incorrect:</p>
                        {result.explanation_incorrect.map((exp, expIdx) => {
                          // Find which option this explanation is for
                          if (expIdx === result.user_answer - (result.user_answer > result.correct_answer ? 1 : 0)) {
                            return <p key={expIdx} className="text-sm text-red-800">{exp}</p>;
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MockExam;
